import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { Radio, ArrowUpRight, ArrowDownLeft, Loader2, Satellite, Zap, ShieldAlert, ScanLine, CheckCircle2, Radar } from "lucide-react";

const TYPE_ICON = {
  "status.online": Satellite, "insight.found": Zap, "threat.detected": ShieldAlert,
  "alert.raised": ShieldAlert, "scan.completed": ScanLine, "job.completed": CheckCircle2,
};
const sevColor = (s) => (s === "warn" ? "#ffb800" : s === "critical" ? "#ff3b30" : "#00e5ff");

const rel = (iso) => {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (isNaN(d)) return "";
  if (d < 60) return `${Math.max(0, Math.floor(d))}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

export default function HiveFeed() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const poll = useRef(null);

  const load = () => api.get("/hive/feed?limit=80").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => {
    load();
    poll.current = setInterval(load, 20000);
    return () => clearInterval(poll.current);
  }, []);

  const events = data?.events || [];
  const ships = data?.ships || [];
  const counts = data?.counts || { in: 0, out: 0 };

  return (
    <div className="px-6 pb-24 pt-20 sm:px-10 lg:px-16" data-testid="hive-feed-page">
      <p className="font-tac text-[11px] tracking-[0.35em] txt-cyan">AVA HIVE BUS · FLEET NERVOUS SYSTEM</p>
      <h1 className="font-head mt-2 text-5xl font-black uppercase tracking-tighter">Fleet Feed</h1>
      <p className="mt-2 max-w-2xl text-sm text-white/50">Every signal OCTON has broadcast to the AVA fleet and every event received from sibling ships — a permanent log of the shared nervous system.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="card-tac p-4" data-testid="feed-stat-out"><p className="font-tac text-[9px] tracking-widest text-white/40">BROADCAST</p><p className="font-head text-4xl font-black txt-cyan">{counts.out}</p></div>
        <div className="card-tac p-4" data-testid="feed-stat-in"><p className="font-tac text-[9px] tracking-widest text-white/40">RECEIVED</p><p className="font-head text-4xl font-black" style={{ color: "#ffb800" }}>{counts.in}</p></div>
        <div className="card-tac p-4"><p className="font-tac text-[9px] tracking-widest text-white/40">SHIPS ONLINE</p><p className="font-head text-4xl font-black">{ships.length}</p></div>
        <div className="card-tac flex items-center gap-2 p-4"><span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-70" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-cyan-400" /></span><p className="text-[11px] text-white/60">Live · polling every 20s</p></div>
      </div>

      {ships.length > 0 && (
        <div className="mt-4 card-tac p-4" data-testid="feed-roster" data-octon-section="hive roster agents fleet" data-octon-label="Hive Roster">
          <div className="mb-2 flex items-center gap-2"><Radar size={14} className="txt-cyan" /><span className="font-tac text-[10px] tracking-widest text-white/60">FLEET ROSTER</span></div>
          <div className="flex flex-wrap gap-2">
            {ships.map((s, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] text-white/70">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.wired || s.online ? "#33d17a" : "#ffb800" }} />
                {s.name || s.ship || s.id || `ship-${i}`}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6">
        {loading ? (
          <div className="flex items-center gap-2 text-white/50"><Loader2 className="animate-spin" size={18} /> Tuning into the hive…</div>
        ) : events.length === 0 ? (
          <div className="card-tac p-8 text-center text-sm text-white/50" data-testid="feed-empty">
            <Radio size={28} className="mx-auto mb-3 text-white/25" />
            No fleet events logged yet. OCTON broadcasts as it learns and advises; sibling-ship events land here as they arrive.
          </div>
        ) : (
          <div className="space-y-2" data-testid="feed-events" data-octon-section="hive feed events shared knowledge" data-octon-label="Hive Feed Events">
            {events.map((e, i) => {
              const Icon = TYPE_ICON[e.type] || Radio;
              const out = e.direction === "out";
              const c = sevColor(e.severity);
              return (
                <motion.div key={e.feed_id || i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.3) }}
                  className="flex items-start gap-3 rounded-xl border border-white/8 bg-white/[0.02] p-3.5" data-testid={`feed-event-${i}`}>
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ background: `${c}14`, color: c }}><Icon size={16} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-tac text-[8px] tracking-widest" style={{ background: out ? "rgba(0,229,255,0.1)" : "rgba(255,184,0,0.12)", color: out ? "#00e5ff" : "#ffb800" }}>
                        {out ? <ArrowUpRight size={9} /> : <ArrowDownLeft size={9} />}{out ? "BROADCAST" : "RECEIVED"}
                      </span>
                      <span className="font-tac text-[9px] tracking-widest text-white/45">{String(e.type || "").toUpperCase()}</span>
                      <span className="text-[10px] text-white/30">· {e.ship}</span>
                      <span className="ml-auto font-mono text-[10px] text-white/30">{rel(e.stored_at || e.ts)}</span>
                    </div>
                    <p className="mt-1 truncate text-[13px] text-white/80">{e.summary}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
